// src/ui/gate/AudioUnlockSplash.tsx — returning-session iOS audio unlock splash.
//
// CONTEXT D-18: cookie-cached returning sessions skip the password gate, so
// they need a separate touchend opportunity to call audioManager.unlock()
// inside a user-gesture frame. This component renders only when:
//   - the boot probe (App.tsx, P6_UNLOCK_BOOT_PROBE_MS) finds Howler.ctx
//     not running 100ms after audioManager.init() ran, AND
//   - the user already passed the gate (so we are post-cookie)
// On Android Chrome the AudioContext usually reports running on first paint
// and this component never renders — by design, no extra UX friction.
//
// UI-SPEC §AudioUnlockSplash visual contract (verbatim):
//   - Full viewport overlay, position:fixed inset:0, z-index:150
//   - Background #0d1f2d (matches gate + boot splash for seamless handoff)
//   - 200x200 splash placeholder + 36px Mouchao wordmark + 16px body
//     "Tap anywhere to enter"
//   - role="dialog" + aria-modal="true" for screen-reader correctness
//   - touchend (and click for desktop) → audioManager.unlock() →
//     fade out 300ms → onDismissed
//
// SOURCE: 06-CONTEXT.md D-18; 06-UI-SPEC.md §AudioUnlockSplash;
//         06-RESEARCH.md §iOS Safari Specifics; 06-PATTERNS.md analog
//         GateScreen.tsx (reuse column + wordmark + vignette layout).

import { useState, type JSX } from 'react';

import './AudioUnlockSplash.css';
import { audioManager } from '../../render/audio/manager';

interface AudioUnlockSplashProps {
  /** Called after the 300ms fade-out completes; parent unmounts the splash. */
  onDismissed(): void;
}

const FADE_OUT_MS = 300;

export function AudioUnlockSplash({
  onDismissed,
}: AudioUnlockSplashProps): JSX.Element {
  const [fading, setFading] = useState(false);

  function handleDismiss(): void {
    if (fading) return;
    // Fire unlock SYNCHRONOUSLY inside the user-gesture frame (D-18). iOS
    // Safari rejects AudioContext.resume() outside a gesture — the
    // touchend/click event IS the gesture, so the call must happen here,
    // not from a setTimeout / promise callback.
    audioManager.unlock();
    setFading(true);
    window.setTimeout(onDismissed, FADE_OUT_MS);
  }

  return (
    <div
      className={`audio-unlock-splash${fading ? ' audio-unlock-splash--fading' : ''}`}
      data-testid="audio-unlock-splash"
      role="dialog"
      aria-modal="true"
      aria-label="Tap to enter Mouchao"
      onTouchEnd={handleDismiss}
      onClick={handleDismiss}
    >
      <div className="audio-unlock-splash__column">
        <img
          className="audio-unlock-splash__vignette"
          src="/splash-placeholder.png"
          alt=""
          aria-hidden="true"
        />
        <h1 className="audio-unlock-splash__wordmark">Mouchao</h1>
        <p className="audio-unlock-splash__body">Tap anywhere to enter</p>
      </div>
    </div>
  );
}
